import { api } from '@/lib/api';
import type {
  RevenueAccount,
  RevenueAccountsResponse,
  RevenueInvoice,
  RevenueInvoiceLine,
  RevenueInvoiceSensor,
  RevenueInvoicesResponse,
  RevenueQueryParams,
  RevenueSummary,
} from './types';

// =============================================================================
// Backend response types (camelCase)
// =============================================================================

interface ApiRevenueAccount {
  id: string;
  accountCode: string;
  accountName: string;
  fullIdentifier: string;
  accountId: string | null;
  totalRevenue: number | string;
  invoiceCount: number;
  sensorCount: number;
}

interface ApiRevenueInvoiceLine {
  id: string;
  category: string;
  sku: string;
  amount: number;
}

interface ApiRevenueInvoiceSensor {
  id: string;
  mongoSensorId: string;
  name: string;
  serialNumber: string | null;
  equipmentType: string | null;
  status: number | null;
  billable: boolean | null;
}

interface ApiRevenueInvoice {
  id: string;
  invoiceNumber: string;
  invAccountId: string;
  accountName: string;
  accountCode: string;
  invoiceDate: string | null;
  month: string | null;
  totalAmount: number;
  sensorCount: number;
  lines: ApiRevenueInvoiceLine[];
  sensors: ApiRevenueInvoiceSensor[];
}

interface ApiRevenueAccountsResponse {
  accounts: ApiRevenueAccount[];
  pageNumber: number;
  pageLength: number;
  total: number;
}

interface ApiRevenueInvoicesResponse {
  invoices: ApiRevenueInvoice[];
  pageNumber: number;
  pageLength: number;
  total: number;
}

interface ApiRevenueSummary {
  totalRevenue: number;
  totalInvoices: number;
  totalAccounts: number;
  byCategory: { category: string; total: number; count: number }[];
}

// =============================================================================
// Transform functions
// =============================================================================

function transformAccount(data: ApiRevenueAccount): RevenueAccount {
  return {
    id: data.id,
    account_code: data.accountCode,
    account_name: data.accountName,
    full_identifier: data.fullIdentifier,
    account_id: data.accountId,
    total_revenue: typeof data.totalRevenue === 'string' ? parseFloat(data.totalRevenue) : data.totalRevenue,
    invoice_count: data.invoiceCount,
    sensor_count: data.sensorCount,
  };
}

function transformInvoiceLine(data: ApiRevenueInvoiceLine): RevenueInvoiceLine {
  return {
    id: data.id,
    category: data.category,
    sku: data.sku,
    amount: data.amount,
  };
}

function transformSensor(data: ApiRevenueInvoiceSensor): RevenueInvoiceSensor {
  return {
    id: data.id,
    mongo_sensor_id: data.mongoSensorId,
    name: data.name,
    serial_number: data.serialNumber,
    equipment_type: data.equipmentType,
    status: data.status,
    billable: data.billable,
  };
}

function transformInvoice(data: ApiRevenueInvoice): RevenueInvoice {
  return {
    id: data.id,
    invoice_number: data.invoiceNumber,
    inv_account_id: data.invAccountId,
    account_name: data.accountName,
    account_code: data.accountCode,
    invoice_date: data.invoiceDate,
    month: data.month,
    total_amount: data.totalAmount,
    sensor_count: data.sensorCount,
    lines: data.lines.map(transformInvoiceLine),
    sensors: data.sensors.map(transformSensor),
  };
}

function transformSummary(data: ApiRevenueSummary): RevenueSummary {
  return {
    total_revenue: data.totalRevenue,
    total_invoices: data.totalInvoices,
    total_accounts: data.totalAccounts,
    by_category: data.byCategory,
  };
}

// =============================================================================
// API functions
// =============================================================================

export async function fetchRevenueAccounts(params?: RevenueQueryParams): Promise<RevenueAccountsResponse> {
  const queryParams: Record<string, unknown> = {};
  if (params?.search) queryParams.search = params.search;
  if (params?.year !== undefined) queryParams.year = params.year;
  if (params?.page_number !== undefined) queryParams.pageNumber = params.page_number;
  if (params?.page_length !== undefined) queryParams.pageLength = params.page_length;
  if (params?.sort_key) queryParams.sortKey = params.sort_key;
  if (params?.sort_direction !== undefined) queryParams.sortDirection = params.sort_direction;

  const data = await api.get<ApiRevenueAccountsResponse>('/v4/revenue/accounts', queryParams);
  return {
    accounts: data.accounts.map(transformAccount),
    page_number: data.pageNumber,
    page_length: data.pageLength,
    total: data.total,
  };
}

export async function fetchRevenueInvoices(params?: RevenueQueryParams): Promise<RevenueInvoicesResponse> {
  const queryParams: Record<string, unknown> = {};
  if (params?.search) queryParams.search = params.search;
  if (params?.category) queryParams.category = params.category;
  if (params?.year !== undefined) queryParams.year = params.year;
  if (params?.month) queryParams.month = params.month;
  if (params?.date_from) queryParams.dateFrom = params.date_from;
  if (params?.date_to) queryParams.dateTo = params.date_to;
  if (params?.inv_account_id) queryParams.invAccountId = params.inv_account_id;
  if (params?.page_number !== undefined) queryParams.pageNumber = params.page_number;
  if (params?.page_length !== undefined) queryParams.pageLength = params.page_length;
  if (params?.sort_key) queryParams.sortKey = params.sort_key;
  if (params?.sort_direction !== undefined) queryParams.sortDirection = params.sort_direction;

  const data = await api.get<ApiRevenueInvoicesResponse>('/v4/revenue', queryParams);
  return {
    invoices: data.invoices.map(transformInvoice),
    page_number: data.pageNumber,
    page_length: data.pageLength,
    total: data.total,
  };
}

export async function fetchRevenueSummary(params?: { year?: number; month?: string }): Promise<RevenueSummary> {
  const queryParams: Record<string, unknown> = {};
  if (params?.year !== undefined) queryParams.year = params.year;
  if (params?.month) queryParams.month = params.month;

  const data = await api.get<ApiRevenueSummary>('/v4/revenue/summary', queryParams);
  return transformSummary(data);
}
